import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Loader2,
  Plus,
  Trash2,
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  GripVertical,
} from 'lucide-react';
import { Button } from '../ui/button';
import { ASSEMBLE_ARTICLE, PUBLISH_ASSEMBLED_ARTICLE } from '../../graphql';
import {
  GET_ASSEMBLER_RECENT,
  GET_ASSEMBLER_SOURCE_PAGE,
  GET_ASSEMBLER_TOPICS,
} from '../../graphql';
import { Loader } from '../common/PageLoader';
import { NoSources, waitingLine } from './noSources';
import { Link, useSearchParams } from 'react-router-dom';
import { useSeesExtractedDetail } from '../settings/supportAccess';
import { decodeEntities } from '../../utils/htmlEntities';
import { nothingReadWords, PRISM_COVERAGE, PrismCoverage } from '../cms/prismCoverage';
import { ARTICLE_STATUS } from '../statusWords';

// The assembler formats and angles are fixed enums that mirror the
// apiserver's ASSEMBLER_FORMATS / ASSEMBLER_ANGLES.
const FORMATS = ['Explainer', 'Timeline', 'Roundup / Listicle', 'Q&A'];
const ANGLES = [
  "What's new",
  'The full picture',
  'How it happened',
  'In depth',
  'Different sides',
  'What to do',
];
// What the format chips say (Prototype v6); the values above are what the server takes.
const FORMAT_LABELS: Record<string, string> = { 'Roundup / Listicle': 'Roundup' };
const formatLabel = (f?: string | null) => (f ? FORMAT_LABELS[f] || f : '');

interface Block {
  heading?: string;
  text?: string;
  /** TinyCMS technical support: the facts a block cites (F1…). */
  fact_ids?: string[];
  /** Everyone else: the source articles it uses (1-based, sourceArticles order). */
  sources?: number[];
}
interface Flag {
  type: string;
  block_number: number | null;
  problematic_text: string;
  cited_fact_id: string;
  explanation: string;
  severity: 'high' | 'medium' | 'low';
}
interface SourceArticle {
  id: string;
  title: string;
  date: string;
}

/**
 * Assemble found nothing to write from (Round 36, backlog ca25a078): a
 * neutral card, not an error, with the way out (choose sources, widen the
 * brief) and what's still waiting to be read.
 */
function NoSourcesCard({
  counts,
  onTopics,
  onArticles,
}: {
  counts: NoSources;
  onTopics: () => void;
  onArticles: () => void;
}) {
  const waiting = waitingLine(counts);
  return (
    <div className="as-nosrc" role="status">
      <div className="as-nosrc-title">Prism AI hasn’t read anything on this yet</div>
      <p className="as-nosrc-body">
        {counts.read != null
          ? `None of the ${counts.read.toLocaleString('en-US')} ${counts.read === 1 ? 'article' : 'articles'} it has read match this brief. `
          : ''}
        Choose sources yourself, or widen the brief, for example a broader topic or a longer stretch
        of dates.
      </p>
      {waiting ? (
        <p className="as-nosrc-wait">
          <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
          {waiting}
        </p>
      ) : null}
      <div className="as-nosrc-actions">
        <button type="button" className="ink-btn" onClick={onTopics}>
          Choose topics &amp; dates
        </button>
        <button type="button" className="ink-btn" onClick={onArticles}>
          Choose specific articles
        </button>
      </div>
    </div>
  );
}

/** Topics & dates' ranges (Prototype): from that many days ago, or all time. */
const RANGES: Array<[Range, string]> = [
  ['7', 'Last 7 days'],
  ['30', 'Last 30 days'],
  ['all', 'All time'],
];
type Range = '7' | '30' | 'all';
/** Topic chips shown before "+ N more" (a newsroom can have dozens). */
const TOPICS_SHOWN = 8;

/** The start of a range, as the server's dateFrom: midnight `days` ago; null for all time. */
export function rangeStart(range: Range, now = new Date()): string | null {
  if (range === 'all') return null;
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - Number(range));
  return d.toISOString();
}

/** How many read articles Specific articles lists at a time. */
const SOURCE_PAGE = 50;
interface Assembled {
  suggestedTitle?: string;
  suggestedExcerpt?: string;
  sourceWarning?: string;
  blocks: Block[];
  sourceArticles: SourceArticle[];
  selectionInfo?: { candidates: number; selected: number; reasoning: string };
  factIndex: Record<string, { fact: string; readable_date?: string; attribution?: string }>;
  dedupStats?: { before: number; after: number; groups_merged: number; removed?: number };
  verification: { passed: boolean; flags: Flag[] };
  expectationSummary?: string;
}

type SourceMode = 'ai' | 'filter' | 'manual';

/**
 * What each check is called and asks for, in plain words (the server's own
 * explanation is for the agent). [F3] is the fact a sentence came from.
 */
const FLAG_WORDS: Record<string, [string, string]> = {
  uncited_claim: [
    'Not linked to a fact',
    'This sentence has a number, a date or a quote but no fact marker like [F3]. Add the marker of the fact it comes from, or rephrase it.',
  ],
  invalid_citation: [
    'Points to a fact that isn’t there',
    'The marker doesn’t match any fact used for this draft. Fix the number or remove it.',
  ],
  fabricated_quote: [
    'Quote not in the source',
    'These quoted words aren’t in the fact it cites. Use the source’s exact words.',
  ],
  duplicate_citation: [
    'Same fact used twice',
    'This fact is already used in another block. Keep it in one place.',
  ],
  numeric_dilution: [
    'Number made vaguer',
    'The source gives an exact number; this says “several” or similar. Use the number.',
  ],
  citation_stacking: [
    'Too many facts in one sentence',
    'Split it so each sentence closely follows one or two facts.',
  ],
  relative_time: [
    'Relative date',
    'Words like “Tuesday” or “tomorrow” go stale. Use the date itself.',
  ],
  outdated_future: ['Out of date', 'It says something is still to come on a date that has passed.'],
  over_word_limit: ['Too long', 'The draft is longer than this format allows.'],
  over_block_limit: ['Too many blocks', 'The draft has more blocks than this format allows.'],
  wrong_listicle_count: [
    'Wrong number of items',
    'The roundup has a different number of items than asked for.',
  ],
  wrong_timeline_count: [
    'Wrong number of entries',
    'The timeline has a different number of entries than asked for.',
  ],
  invalid_timeline_heading: [
    'Entry needs a date',
    'Start the heading with its date, like “April 7, 2026 — Ceasefire announced”.',
  ],
  missing_timeline_label: [
    'Entry needs a label',
    'Add a short label after the date, like “— Ceasefire announced”.',
  ],
  timeline_order: ['Out of order', 'The entries aren’t in the order you chose.'],
  duplicate_summary_lead: [
    'Summary repeats the opening',
    'Say something different in the summary.',
  ],
  duplicate_summary_body: ['Summary repeats the text', 'Say something different in the summary.'],
  duplicate_lead_body: ['Opening is repeated', 'The first block repeats a later one.'],
  thin_bottom_line: ['Ending is thin', 'The last block needs a clearer takeaway.'],
};
const flagWords = (f: Flag): [string, string] =>
  FLAG_WORDS[f.type] || [f.type.replace(/_/g, ' '), f.explanation];

/**
 * The same checks named by article, for everyone but TinyCMS technical support
 * (backlog e3df0116): the draft's markers point at source articles ([2]), not
 * at the facts behind them, so the words do too.
 */
const ARTICLE_FLAG_WORDS: Record<string, [string, string]> = {
  uncited_claim: [
    'Not in your articles',
    'This sentence has a number, a date or a quote but no source marker like [2]. Add the marker of the article it comes from, or rephrase it.',
  ],
  invalid_citation: [
    'Points to an article that isn’t there',
    'The marker doesn’t match any article used for this draft. Fix the number or remove it.',
  ],
  fabricated_quote: [
    'Quote not in the source',
    'These quoted words aren’t in the article it cites. Use the article’s exact words.',
  ],
  duplicate_citation: [
    'Same point used twice',
    'This point from the article is already used in another block. Keep it in one place.',
  ],
  numeric_dilution: [
    'Number made vaguer',
    'The article it comes from gives an exact number; this says “several” or similar. Use the number.',
  ],
  citation_stacking: [
    'Too much in one sentence',
    'Split it so each sentence closely follows one or two points from your articles.',
  ],
};
const articleFlagWords = (f: Flag): [string, string] => ARTICLE_FLAG_WORDS[f.type] || flagWords(f);

/** Draft markers, [F3] or [2] / [2, 4], are left out of the article. */
const stripMarkers = (s: string) => s.replace(/\s*\[F?\d+(?:\s*,\s*F?\d+)*\]/g, '');

const MARKERS = /\[(F?\d+(?:\s*,\s*F?\d+)*)\]/g;

/**
 * A paragraph's sentences, each with its markers ("… in Game 2 [2]." stays
 * one sentence). Splits only where the next sentence starts with a capital,
 * a digit or an opening quote, so "2.91" and "[2, 4]" don't break.
 */
const sentencesOf = (para: string): string[] =>
  para
    .split(/(?<=[.!?…]["”’)]?(?:\s*\[F?\d+(?:\s*,\s*F?\d+)*\])?)\s+(?=["“‘(]?[A-Z0-9])/)
    .filter((x) => x.trim());

/** The marker tokens in a sentence: "2", "F3". */
const markerTokens = (sentence: string): string[] =>
  [...sentence.matchAll(MARKERS)].flatMap((m) => m[1].split(/\s*,\s*/));

const SEVERITY_STYLES: Record<string, string> = {
  high: 'border-red-500/40 bg-red-500/10 text-red-300',
  medium: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  low: 'border-cmp-line-2/40 bg-cmp-surface/10 text-cmp-ink',
};

const inputClass =
  'w-full rounded border border-cmp-line-2 bg-core-dark text-cmp-ink px-3 py-2 text-sm focus:outline-none focus:border-primary';

interface AssembledDraft {
  id: string;
  title?: string | null;
  publishStatus: string;
  format?: string | null;
  sourceCount: number;
  createdAt?: string | null;
}

// A saved assembly waits as a draft until someone reviews and publishes it.
const RECENT_STATUS: Record<string, { label: string; color: string; dot: string }> = {
  draft: { label: ARTICLE_STATUS.review, color: 'var(--ink-amber)', dot: 'var(--ink-amber)' },
  scheduled: {
    label: ARTICLE_STATUS.scheduled,
    color: 'var(--ink-accent)',
    dot: 'var(--ink-accent)',
  },
  published: { label: ARTICLE_STATUS.live, color: 'var(--ink-green-ink)', dot: 'var(--ink-green)' },
};

const shortDay = (v?: string | null) => {
  if (!v) return '';
  const d = new Date(/^\d{10,}$/.test(v) ? Number(v) : v);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/** RECENT's date (Prototype): "today 10:12 AM", else "Oct 8". */
const recentWhen = (v?: string | null) => {
  if (!v) return '';
  const d = new Date(/^\d{10,}$/.test(v) ? Number(v) : v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toDateString() === new Date().toDateString()
    ? `today ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
    : shortDay(v);
};

export function AssemblerWizard({ accountId }: { accountId: string }) {
  // Fact-by-fact view for TinyCMS technical support; source articles for everyone else.
  const seesExtracted = useSeesExtractedDetail(accountId);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1 — brief & sources
  const [userNeed, setUserNeed] = useState('');
  const [format, setFormat] = useState(FORMATS[0]);
  // Reader questions → "Draft an article →" opens here with the question as the brief.
  const [searchParams] = useSearchParams();
  const [brief, setBrief] = useState(() => (searchParams.get('brief') || '').slice(0, 2000));
  const [sourceMode, setSourceMode] = useState<SourceMode>('ai');
  const [listicleCount, setListicleCount] = useState(5);
  const [timelineCount, setTimelineCount] = useState(7);
  const [timelineOrder, setTimelineOrder] = useState<'chronological' | 'reverse'>('chronological');
  // Topics & dates (backlog 8260acbd): topic chips from the site's own topics
  // and a preset range, as the design has, not free text and date pickers.
  const [topicsPicked, setTopicsPicked] = useState<string[]>([]);
  const [range, setRange] = useState<Range>('30');
  const [allTopics, setAllTopics] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Result
  const [result, setResult] = useState<Assembled | null>(null);
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  // Edited since the checks ran: they describe the text as it was assembled.
  const [editedSinceCheck, setEditedSinceCheck] = useState(false);
  const [published, setPublished] = useState<{ id: string; title: string } | null>(null);
  const [error, setError] = useState('');
  // Assemble came back with nothing to write from (Round 36): the card's
  // counts, until the brief changes or it's assembled again.
  const [noSources, setNoSources] = useState<NoSources | null>(null);
  // Step 2's Sources panel: the source article (1-based) whose sentences are highlighted.
  const [selSource, setSelSource] = useState<number | null>(null);

  const isListicle = /listicle|roundup/i.test(format);
  const isTimeline = /timeline/i.test(format);

  // Specific articles: every read article is reachable, a page at a time, and
  // a title search narrows them (backlog 62681541). Before, the list stopped
  // at the newest 50.
  const apollo = useApolloClient();
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourcePage, setSourcePage] = useState<{ items: SourceArticle[]; total: number } | null>(
    null
  );
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const sourceRequest = useRef(0);
  const { data: recentData, refetch: refetchRecent } = useQuery<{
    assemblerRecent: AssembledDraft[];
  }>(GET_ASSEMBLER_RECENT, {
    variables: { accountId, limit: 8 },
    fetchPolicy: 'cache-and-network',
  });
  const recent = recentData?.assemblerRecent || [];
  // Write drafts only from articles Prism AI has read (pages aren't used): with
  // none read, say so and why, and hold Assemble (UX review round 22).
  const { data: coverageData } = useQuery<{ researchOverview: PrismCoverage }>(PRISM_COVERAGE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const nothingRead = coverageData?.researchOverview
    ? nothingReadWords(coverageData.researchOverview)
    : null;
  const { data: topicsData } = useQuery<{
    assemblerTopics: Array<{ name: string; count: number }>;
  }>(GET_ASSEMBLER_TOPICS, {
    variables: { accountId },
    skip: !accountId || sourceMode !== 'filter',
  });
  const siteTopics = topicsData?.assemblerTopics || [];
  const [assemble, { loading: assembling }] = useMutation(ASSEMBLE_ARTICLE);
  const [publish, { loading: publishing }] = useMutation(PUBLISH_ASSEMBLED_ARTICLE);

  const sources: SourceArticle[] = sourcePage?.items || [];

  // A first page (offset 0) replaces the list; Show more adds the next one.
  // Only the latest request lands, so a slow search can't overwrite a newer one.
  const loadSourcePage = async (search: string, offset: number) => {
    const n = ++sourceRequest.current;
    setSourcesLoading(true);
    try {
      const { data } = await apollo.query<{
        assemblerSourcePage: { items: SourceArticle[]; total: number };
      }>({
        query: GET_ASSEMBLER_SOURCE_PAGE,
        variables: { accountId, search: search.trim() || null, offset, limit: SOURCE_PAGE },
        fetchPolicy: 'network-only',
      });
      if (n !== sourceRequest.current) return;
      const page = data.assemblerSourcePage;
      setSourcePage((prev) =>
        offset && prev ? { items: [...prev.items, ...page.items], total: page.total } : page
      );
    } catch (e: any) {
      if (n === sourceRequest.current) setError(e.message || 'Couldn’t load the articles.');
    } finally {
      if (n === sourceRequest.current) setSourcesLoading(false);
    }
  };

  // The list loads when Specific articles opens, and again as the search
  // changes (after a pause in the typing).
  useEffect(() => {
    if (sourceMode !== 'manual' || !accountId) return;
    const t = window.setTimeout(() => void loadSourcePage(sourceSearch, 0), sourceSearch ? 300 : 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode, sourceSearch, accountId]);

  const runAssemble = async () => {
    setError('');
    setNoSources(null);
    if (!brief.trim()) {
      setError('Say what it should cover first.');
      return;
    }
    // Straight to "Check the draft", with a card while it's assembled (round 13).
    setResult(null);
    setSelSource(null);
    setStep(2);
    try {
      const { data } = await assemble({
        variables: {
          accountId,
          input: {
            brief,
            userNeed,
            format,
            sourceMode,
            topicIds: sourceMode === 'manual' ? selectedIds : [],
            categories: sourceMode === 'filter' ? topicsPicked : [],
            dateFrom: sourceMode === 'filter' ? rangeStart(range) : null,
            listicleCount: isListicle ? listicleCount : 0,
            timelineCount: isTimeline ? timelineCount : 0,
            timelineOrder: isTimeline ? timelineOrder : 'chronological',
          },
        },
      });
      const r: Assembled = data.assembleArticle;
      setResult(r);
      setEditedSinceCheck(false);
      setTitle(r.suggestedTitle || '');
      setExcerpt(r.suggestedExcerpt || '');
      setStep(2);
    } catch (e: any) {
      setStep(1);
      const ext = e?.graphQLErrors?.[0]?.extensions;
      if (ext?.reason === 'NO_SOURCES') {
        setNoSources({
          read: typeof ext.read === 'number' ? ext.read : null,
          unreadRecent: Number(ext.unreadRecent) || 0,
          unreadOlder: Number(ext.unreadOlder) || 0,
        });
      } else setError(e.message || 'Assembly failed.');
    }
  };

  // Which source article a marker points at: [2] is article 2; for technical
  // support, [F3] is the article fact F3 came from.
  const articleOf = useMemo(() => {
    const articles = result?.sourceArticles || [];
    const place = new Map(articles.map((a, i) => [String(a.id), i + 1]));
    const byTitle = new Map(articles.map((a, i) => [String(a.title || '').toLowerCase(), i + 1]));
    const facts = (result?.factIndex || {}) as Record<string, { post_id?: string; title?: string }>;
    return (token: string): number | null => {
      if (/^\d+$/.test(token)) {
        const k = Number(token);
        return k >= 1 && k <= articles.length ? k : null;
      }
      const f = facts[token];
      if (!f) return null;
      return (
        place.get(String(f.post_id)) || byTitle.get(String(f.title || '').toLowerCase()) || null
      );
    };
  }, [result]);

  // How many sentences use each source article, for the Sources panel.
  const sentenceUses = useMemo(() => {
    const uses = new Map<number, number>();
    for (const b of result?.blocks || []) {
      for (const para of (b.text || '').split(/\n{2,}/)) {
        for (const sentence of sentencesOf(para)) {
          const ks = new Set(
            markerTokens(sentence)
              .map(articleOf)
              .filter((k) => k != null)
          );
          ks.forEach((k) => uses.set(k as number, (uses.get(k as number) || 0) + 1));
        }
      }
    }
    return uses;
  }, [result, articleOf]);

  // While assembling: how many articles it's built from, when that's known already.
  const assemblingFrom =
    sourceMode === 'manual' && selectedIds.length
      ? `Assembling from ${selectedIds.length} ${selectedIds.length === 1 ? 'article' : 'articles'}…`
      : 'Assembling from your articles…';

  const updateBlock = (i: number, field: 'heading' | 'text', value: string) => {
    setEditedSinceCheck(true);
    setResult((prev) =>
      prev
        ? {
            ...prev,
            blocks: prev.blocks.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)),
          }
        : prev
    );
  };
  const addBlock = () => {
    setEditedSinceCheck(true);
    setResult((prev) =>
      prev ? { ...prev, blocks: [...prev.blocks, { heading: '', text: '' }] } : prev
    );
  };
  const removeBlock = (i: number) => {
    setEditedSinceCheck(true);
    setResult((prev) =>
      prev ? { ...prev, blocks: prev.blocks.filter((_, idx) => idx !== i) } : prev
    );
  };
  // Reorder blocks by drag-and-drop (functional update keeps it stable mid-drag).
  const moveBlock = (from: number, to: number) => {
    setResult((prev) => {
      if (!prev) return prev;
      const blocks = [...prev.blocks];
      const [moved] = blocks.splice(from, 1);
      blocks.splice(to, 0, moved);
      return { ...prev, blocks };
    });
  };

  const renderPreviewBody = (blocks: Block[]) =>
    blocks.map((b, i) => {
      const text = stripMarkers(b.text || '');
      return (
        <div key={i} className="mb-4">
          {b.heading ? (
            <h2 className="text-cmp-ink text-lg font-semibold mb-1">{b.heading}</h2>
          ) : null}
          {text.split(/\n{2,}/).map((p, pi) => (
            <p key={pi} className="text-cmp-ink mb-2 leading-relaxed">
              {p}
            </p>
          ))}
        </div>
      );
    });

  const wordCount = useMemo(() => {
    if (!result) return 0;
    return result.blocks.reduce(
      (n, b) => n + ((b.text || '').match(/[A-Za-z0-9']+/g)?.length || 0),
      0
    );
  }, [result]);

  const runPublish = async () => {
    if (!result) return;
    setError('');
    if (!title.trim()) {
      setError('Headline is required.');
      return;
    }
    try {
      const { data } = await publish({
        variables: {
          accountId,
          input: {
            title,
            excerpt,
            blocks: result.blocks,
            // Always a draft: it waits in Content for checks and approval.
            status: 'draft',
            sourceIds: result.sourceArticles.map((s) => s.id),
            brief,
            assemblyFormat: format,
          },
        },
      });
      setPublished(data.publishAssembledArticle);
      void refetchRecent();
    } catch (e: any) {
      setError(e.message || 'Publish failed.');
    }
  };

  const resetAll = () => {
    setStep(1);
    setResult(null);
    setPublished(null);
    setTitle('');
    setExcerpt('');
    setError('');
  };

  return (
    // Scrolls with the page, under the frosted top bar.
    <div className="flex-1">
      {/* Step indicator (Prototype v14), from the brief on: so it's clear a review step follows. */}
      <ol className="as-steps" aria-label="Steps">
        {(
          [
            [1, 'Brief and sources'],
            [2, 'Check the draft'],
            [3, 'Send for review'],
          ] as const
        ).map(([n, label], idx) => {
          const done = step > n || (n === 3 && Boolean(published));
          const current = step === n && !done;
          return (
            <React.Fragment key={n}>
              <li
                className="as-step"
                data-state={done ? 'done' : current ? 'current' : undefined}
                aria-current={current ? 'step' : undefined}
              >
                <span className="as-step-n">{done ? '✓' : n}</span>
                {label}
              </li>
              {idx < 2 ? <li className="as-step-sep" aria-hidden="true" /> : null}
            </React.Fragment>
          );
        })}
      </ol>
      <div className="as-layout">
        <div className="as-main">
          {error && (
            <div className="mb-4 rounded border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          {step === 1 && noSources ? (
            <NoSourcesCard
              counts={noSources}
              onTopics={() => setSourceMode('filter')}
              onArticles={() => setSourceMode('manual')}
            />
          ) : null}

          {/* ── Step 1 (Prototype v6: brief, format, angle, sources) ── */}
          {step === 1 && (
            <div className="as-brief">
              <label className="as-label" htmlFor="as-brief">
                What should it cover?
              </label>
              <textarea
                id="as-brief"
                rows={4}
                className="as-textarea"
                value={brief}
                onChange={(e) => {
                  setBrief(e.target.value);
                  setNoSources(null);
                }}
                placeholder="What should it cover?"
              />

              <div className="as-label">Format</div>
              <div className="as-chips">
                {FORMATS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className="as-chip"
                    data-active={format === f ? 'true' : undefined}
                    onClick={() => setFormat(f)}
                  >
                    {FORMAT_LABELS[f] || f}
                  </button>
                ))}
              </div>
              {isListicle ? (
                <label className="as-inline">
                  Items
                  <input
                    type="number"
                    min={3}
                    max={50}
                    className="as-number"
                    value={listicleCount}
                    onChange={(e) => setListicleCount(parseInt(e.target.value, 10) || 5)}
                  />
                </label>
              ) : null}
              {isTimeline ? (
                <div className="as-inline">
                  <label>
                    Events
                    <input
                      type="number"
                      min={4}
                      max={15}
                      className="as-number"
                      value={timelineCount}
                      onChange={(e) => setTimelineCount(parseInt(e.target.value, 10) || 7)}
                    />
                  </label>
                  <span className="ink-seg as-seg">
                    {(
                      [
                        ['chronological', 'Oldest first'],
                        ['reverse', 'Newest first'],
                      ] as const
                    ).map(([v, label]) => (
                      <button
                        key={v}
                        type="button"
                        data-active={timelineOrder === v ? 'true' : 'false'}
                        onClick={() => setTimelineOrder(v)}
                      >
                        {label}
                      </button>
                    ))}
                  </span>
                </div>
              ) : null}

              <div className="as-label">
                Angle <span className="as-optional">· optional</span>
              </div>
              <div className="as-chips">
                {ANGLES.map((u) => (
                  <button
                    key={u}
                    type="button"
                    className="as-chip"
                    data-active={userNeed === u ? 'true' : undefined}
                    onClick={() => setUserNeed(userNeed === u ? '' : u)}
                  >
                    {u}
                  </button>
                ))}
              </div>

              <div className="as-label">Sources</div>
              <span className="ink-seg as-seg">
                {(
                  [
                    ['ai', 'Agent picks'],
                    ['filter', 'Topics & dates'],
                    ['manual', 'Specific articles'],
                  ] as [SourceMode, string][]
                ).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    data-active={sourceMode === mode ? 'true' : 'false'}
                    onClick={() => setSourceMode(mode)}
                  >
                    {label}
                  </button>
                ))}
              </span>
              {sourceMode === 'ai' ? (
                <p className="as-help">
                  Your agent picks the most relevant articles Prism AI has read, from your brief.
                </p>
              ) : null}
              {sourceMode === 'filter' ? (
                <div
                  className="as-chips as-filter-chips"
                  role="group"
                  aria-label="Topics and dates"
                >
                  {(allTopics ? siteTopics : siteTopics.slice(0, TOPICS_SHOWN)).map((t) => {
                    const on = topicsPicked.includes(t.name);
                    return (
                      <button
                        key={t.name}
                        type="button"
                        className="as-chip"
                        aria-pressed={on}
                        data-active={on ? 'true' : undefined}
                        onClick={() =>
                          setTopicsPicked((prev) =>
                            on ? prev.filter((x) => x !== t.name) : [...prev, t.name]
                          )
                        }
                      >
                        {t.name}
                      </button>
                    );
                  })}
                  {siteTopics.length > TOPICS_SHOWN ? (
                    <button
                      type="button"
                      className="ink-link-btn as-chip-more"
                      onClick={() => setAllTopics((v) => !v)}
                    >
                      {allTopics ? 'Fewer' : `+ ${siteTopics.length - TOPICS_SHOWN} more`}
                    </button>
                  ) : null}
                  {siteTopics.length ? <span className="as-chip-rule" aria-hidden="true" /> : null}
                  {RANGES.map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      className="as-chip"
                      aria-pressed={range === k}
                      data-active={range === k ? 'true' : undefined}
                      onClick={() => setRange(k)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
              {sourceMode === 'filter' ? (
                <p className="as-help">
                  {siteTopics.length
                    ? 'Only articles Prism AI has read, in these topics and dates.'
                    : 'Only articles Prism AI has read, in these dates.'}
                </p>
              ) : null}
              {sourceMode === 'manual' ? (
                <div className="as-manual">
                  <p className="as-help">
                    Only the articles you check. Only articles Prism AI has read are listed.
                  </p>
                  <input
                    type="search"
                    className="as-input as-source-search"
                    placeholder="Search by title"
                    aria-label="Search read articles by title"
                    value={sourceSearch}
                    onChange={(e) => setSourceSearch(e.target.value)}
                  />
                  {sourcePage ? (
                    <p className="as-source-count" role="status">
                      {sourceCountWords(sources.length, sourcePage.total, sourceSearch.trim())}
                      {selectedIds.length ? ` · ${selectedIds.length} checked` : ''}
                    </p>
                  ) : sourcesLoading ? (
                    <Loader size="inline" />
                  ) : null}
                  {sources.length ? (
                    <div className="as-source-list">
                      {sources.map((s) => (
                        <label key={s.id} className="as-source">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(s.id)}
                            onChange={(e) =>
                              setSelectedIds((prev) =>
                                e.target.checked ? [...prev, s.id] : prev.filter((x) => x !== s.id)
                              )
                            }
                          />
                          <span className="as-source-title">{decodeEntities(s.title)}</span>
                          <span className="as-source-date">{shortDay(s.date)}</span>
                        </label>
                      ))}
                      {sources.length < (sourcePage?.total || 0) ? (
                        <button
                          type="button"
                          className="as-source-more"
                          disabled={sourcesLoading}
                          onClick={() => void loadSourcePage(sourceSearch, sources.length)}
                        >
                          {sourcesLoading
                            ? 'Loading…'
                            : `Show ${Math.min(SOURCE_PAGE, sourcePage!.total - sources.length)} more`}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {nothingRead ? (
                <p className="as-nothing-read" role="status">
                  {nothingRead}
                </p>
              ) : null}
              <div className="as-go">
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  onClick={runAssemble}
                  disabled={assembling || !brief.trim() || Boolean(nothingRead)}
                >
                  Assemble draft
                </button>
                <span className="as-help" style={{ margin: 0 }}>
                  Next you check the draft sentence by sentence. Nothing is published from Write.
                </span>
              </div>
            </div>
          )}

          {/* ── Step 2, while it's assembled (Prototype, round 13) ── */}
          {step === 2 && !result && (
            <div className="as-working" role="status" aria-live="polite">
              <div className="as-working-head">{assemblingFrom}</div>
              <div className="as-working-note">
                {seesExtracted
                  ? 'Every sentence gets linked to the fact it came from. Anything a fact doesn’t back up gets flagged for you.'
                  : 'Every sentence gets linked to the article it came from. Anything your articles don’t back up gets flagged for you.'}
              </div>
              <div className="as-skeleton" aria-hidden="true">
                {[96, 88, 72, 91, 64].map((w, i) => (
                  <span key={i} style={{ width: `${w}%` }} />
                ))}
              </div>
            </div>
          )}

          {/* ── Step 2 ── */}
          {step === 2 && result && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-cmp-ink text-sm">
                  {result.blocks.length} blocks · {wordCount} words
                  {result.dedupStats
                    ? ` · ${result.dedupStats.after} of ${result.dedupStats.before} facts used${
                        result.dedupStats.before > result.dedupStats.after
                          ? ` (${result.dedupStats.before - result.dedupStats.after} duplicates left out)`
                          : ''
                      }`
                    : ''}
                </div>
                <Button variant="outline" onClick={addBlock}>
                  <Plus className="w-4 h-4 mr-1" /> Add block
                </Button>
              </div>

              <VerificationPanel
                verification={result.verification}
                edited={editedSinceCheck}
                byArticle={!seesExtracted}
              />

              <p className="text-cmp-ink-3 text-xs">
                {seesExtracted
                  ? 'Drag the handle to reorder blocks. The numbers after a sentence are the articles its facts come from (Edit shows the [F3] markers); markers are left out of the article.'
                  : 'Drag the handle to reorder blocks. The numbers after a sentence are the articles it comes from. Select one to see it on the right; markers are left out of the article.'}
              </p>
              <DndProvider backend={HTML5Backend}>
                <div className="space-y-3">
                  {result.blocks.map((b, i) => (
                    <SortableBlock
                      key={i}
                      index={i}
                      block={b}
                      moveBlock={moveBlock}
                      updateBlock={updateBlock}
                      removeBlock={removeBlock}
                      sourceArticles={result.sourceArticles}
                      articleOf={articleOf}
                      selSource={selSource}
                      onSelectSource={(k) => setSelSource((cur) => (cur === k ? null : k))}
                    />
                  ))}
                </div>
              </DndProvider>

              <div className="flex items-center justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to brief
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  className="bg-button-primary text-button-primary"
                >
                  Preview article <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 3 ── */}
          {step === 3 && result && (
            <div className="space-y-4 max-w-3xl">
              {published ? (
                <div className="ink-card" style={{ padding: 18 }}>
                  <div
                    className="flex items-center gap-2 mb-1"
                    style={{ color: 'var(--ink-green-ink)', fontWeight: 600 }}
                  >
                    <CheckCircle2 className="w-5 h-5" /> Draft saved
                  </div>
                  <p className="text-sm" style={{ color: 'var(--ink-ink)' }}>
                    “{published.title}” is in Website, waiting for your review. Nothing goes live
                    until you approve it.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Link to={`/content/${published.id}`} className="ink-btn ink-btn-primary">
                      Open article
                    </Link>
                    <button type="button" className="ink-btn" onClick={resetAll}>
                      Assemble another
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-cmp-ink text-sm font-medium mb-1">Headline</label>
                    <input
                      className={inputClass}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-cmp-ink text-sm font-medium mb-1">Summary</label>
                    <textarea
                      rows={2}
                      className={inputClass}
                      value={excerpt}
                      onChange={(e) => setExcerpt(e.target.value)}
                    />
                  </div>

                  <div className="rounded border border-cmp-line-2 bg-core-dark p-4">
                    <h1 className="text-cmp-ink text-xl font-bold mb-1">{title}</h1>
                    {excerpt && <p className="text-cmp-ink-2 italic mb-4">{excerpt}</p>}
                    {renderPreviewBody(result.blocks)}
                  </div>

                  <div className="text-cmp-ink-2 text-xs">
                    {wordCount} words · drawn from {result.sourceArticles.length} source articles
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <Button variant="outline" onClick={() => setStep(2)}>
                      <ArrowLeft className="w-4 h-4 mr-1" /> Back to review
                    </Button>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={runPublish}
                        disabled={publishing}
                        className="bg-button-primary text-button-primary"
                      >
                        {publishing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                          </>
                        ) : (
                          'Save draft for review'
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {step === 2 && result ? (
          <SourcesPanel
            articles={result.sourceArticles}
            uses={sentenceUses}
            selected={selSource}
            onSelect={(k) => setSelSource((cur) => (cur === k ? null : k))}
          />
        ) : step === 2 ? null : (
          <aside className="as-recent" aria-label="Recent">
            <div className="as-recent-head">RECENT</div>
            {recent.length ? (
              recent.map((r) => {
                const st = RECENT_STATUS[r.publishStatus] || RECENT_STATUS.draft;
                return (
                  <Link key={r.id} to={`/content/${r.id}`} className="as-recent-row">
                    <span className="as-recent-title">
                      {decodeEntities(r.title) || 'Untitled draft'}
                    </span>
                    <span className="as-recent-meta">
                      <span style={{ color: st.color }}>
                        <span className="ink-dot" style={{ background: st.dot }} />
                        {st.label}
                      </span>
                      <span>
                        · {/* status · format · sources · when (Prototype) */}
                        {[
                          formatLabel(r.format),
                          `${r.sourceCount} ${r.sourceCount === 1 ? 'source' : 'sources'}`,
                          recentWhen(r.createdAt),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </span>
                  </Link>
                );
              })
            ) : (
              <p className="as-recent-none">
                Nothing assembled yet. Drafts you save land here and in Website.
              </p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

const BLOCK_DND_TYPE = 'ASSEMBLER_BLOCK';

function SortableBlock({
  index,
  block,
  moveBlock,
  updateBlock,
  removeBlock,
  sourceArticles,
  articleOf,
  selSource,
  onSelectSource,
}: {
  index: number;
  block: Block;
  moveBlock: (from: number, to: number) => void;
  updateBlock: (i: number, field: 'heading' | 'text', value: string) => void;
  removeBlock: (i: number) => void;
  sourceArticles: SourceArticle[];
  articleOf: (token: string) => number | null;
  selSource: number | null;
  onSelectSource: (k: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  // Read as sentences, with the articles each one uses; Edit shows the raw text.
  const [editing, setEditing] = useState(false);

  const [, drop] = useDrop<{ index: number }>({
    accept: BLOCK_DND_TYPE,
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const rect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (rect.bottom - rect.top) / 2;
      const offset = monitor.getClientOffset();
      if (!offset) return;
      const hoverClientY = offset.y - rect.top;
      // Only cross the midpoint to avoid flicker when dragging over a neighbor.
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      moveBlock(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: BLOCK_DND_TYPE,
    item: () => ({ index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  // The whole card is the drop target + drag preview; only the grip drags.
  preview(drop(ref));
  drag(handleRef);

  const uses = (sentence: string) => {
    const ks: number[] = [];
    for (const t of markerTokens(sentence)) {
      const k = articleOf(t);
      if (k != null && !ks.includes(k)) ks.push(k);
    }
    return ks;
  };
  const highlighted =
    selSource != null &&
    (block.text || '')
      .split(/\n{2,}/)
      .some((p) => sentencesOf(p).some((x) => uses(x).includes(selSource)));

  return (
    <div
      ref={ref}
      className={`as-block rounded border border-cmp-line-2 bg-core-dark p-3 ${isDragging ? 'opacity-40' : ''}`}
      data-active={highlighted ? 'true' : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button
            ref={handleRef}
            className="cursor-move text-cmp-ink-3 hover:text-cmp-ink"
            aria-label="Drag to reorder block"
            title="Drag to reorder"
            type="button"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-cmp-ink-3 text-xs">Block {index + 1}</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="as-block-edit"
            aria-expanded={editing}
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? 'Done' : 'Edit'}
          </button>
          <button
            onClick={() => removeBlock(index)}
            className="text-cmp-ink-3 hover:text-red-400"
            aria-label="Remove block"
            type="button"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      {editing ? (
        <>
          <input
            className={`${inputClass} mb-2`}
            placeholder="Heading"
            aria-label="Heading (optional)"
            value={block.heading || ''}
            onChange={(e) => updateBlock(index, 'heading', e.target.value)}
          />
          <textarea
            rows={5}
            className={inputClass}
            value={block.text || ''}
            onChange={(e) => updateBlock(index, 'text', e.target.value)}
          />
        </>
      ) : (
        <div className="as-block-read">
          {block.heading ? <div className="as-block-heading">{block.heading}</div> : null}
          {(block.text || '').split(/\n{2,}/).map((para, pi) => (
            <p key={pi} className="as-block-para">
              {sentencesOf(para).map((sentence, si) => {
                const ks = uses(sentence);
                return (
                  <React.Fragment key={si}>
                    {si ? ' ' : ''}
                    <span
                      className="as-sent"
                      data-hl={selSource != null && ks.includes(selSource) ? 'true' : undefined}
                    >
                      {stripMarkers(sentence)}
                      {ks.map((k) => (
                        <button
                          key={k}
                          type="button"
                          className="as-cite"
                          data-active={selSource === k ? 'true' : undefined}
                          title={decodeEntities(sourceArticles[k - 1]?.title) || undefined}
                          aria-label={`Source ${k}: ${decodeEntities(sourceArticles[k - 1]?.title) || 'article'}`}
                          onClick={() => onSelectSource(k)}
                        >
                          {k}
                        </button>
                      ))}
                    </span>
                  </React.Fragment>
                );
              })}
            </p>
          ))}
        </div>
      )}
      {block.fact_ids && block.fact_ids.length > 0 && (
        <div className="mt-1 text-xs text-cmp-ink-3">Cites: {block.fact_ids.join(', ')}</div>
      )}
    </div>
  );
}

/**
 * Step 2, right column (Prototype, round 13): the articles the draft is built
 * from, how many sentences use each, and selecting one highlights them.
 */
function SourcesPanel({
  articles,
  uses,
  selected,
  onSelect,
}: {
  articles: SourceArticle[];
  uses: Map<number, number>;
  selected: number | null;
  onSelect: (k: number) => void;
}) {
  return (
    <aside className="as-recent as-sources" aria-label="Sources">
      <div className="as-recent-head">
        SOURCES · {articles.length} {articles.length === 1 ? 'ARTICLE' : 'ARTICLES'}
      </div>
      {articles.map((a, i) => {
        const k = i + 1;
        const n = uses.get(k) || 0;
        const sel = selected === k;
        return (
          <div key={a.id} className="as-src" data-active={sel ? 'true' : undefined}>
            <button
              type="button"
              className="as-src-pick"
              aria-pressed={sel}
              onClick={() => onSelect(k)}
            >
              <span className="as-src-n">{k}</span>
              <span className="as-src-body">
                <span className="as-src-title">
                  {decodeEntities(a.title) || 'Untitled article'}
                </span>
                <span className="as-src-meta">
                  {[shortDay(a.date), `used in ${n} ${n === 1 ? 'sentence' : 'sentences'}`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </button>
            {sel ? (
              <Link to={`/content/${a.id}`} className="as-src-open">
                Open article →
              </Link>
            ) : null}
          </div>
        );
      })}
      <p className="as-recent-none">
        Every sentence comes from these articles. Select one to highlight the sentences that use it.
      </p>
    </aside>
  );
}

function VerificationPanel({
  verification,
  edited = false,
  byArticle = false,
}: {
  verification: { passed: boolean; flags: Flag[] };
  /** Edited since assembly: the checks describe the text as it was then. */
  edited?: boolean;
  /** Name sources by article, not fact (everyone but TinyCMS technical support). */
  byArticle?: boolean;
}) {
  const words = byArticle ? articleFlagWords : flagWords;
  const flags = verification?.flags || [];
  const stale = edited ? (
    <div className="text-xs mt-1" style={{ color: 'var(--ink-ink2)' }}>
      You’ve edited the draft since these checks ran, so they may no longer apply. The article’s own
      checks run again before you approve it.
    </div>
  ) : null;
  if (verification?.passed || flags.length === 0) {
    return (
      <div className="ink-card text-sm" style={{ padding: '10px 14px' }}>
        <div className="flex items-center gap-2" style={{ color: 'var(--ink-green-ink)' }}>
          <CheckCircle2 className="w-4 h-4" /> Checks passed: every claim links to{' '}
          {byArticle ? 'a source article' : 'a fact'} and the draft is within limits.
        </div>
        {stale}
      </div>
    );
  }
  const order = { high: 0, medium: 1, low: 2 } as const;
  const sorted = [...flags].sort((a, b) => order[a.severity] - order[b.severity]);
  return (
    <div className="rounded border border-cmp-line-2 bg-core-dark p-3">
      <div className="flex items-center gap-2 text-sm text-amber-300 mb-2">
        <AlertTriangle className="w-4 h-4" /> {flags.length}{' '}
        {flags.length === 1 ? 'thing to check' : 'things to check'} before you save
      </div>
      {stale}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {sorted.map((f, i) => (
          <div
            key={i}
            className={`rounded border px-3 py-2 text-xs ${SEVERITY_STYLES[f.severity]}`}
          >
            <div className="font-medium">
              {words(f)[0]}
              {f.block_number ? ` · Block ${f.block_number}` : ''}
            </div>
            {f.problematic_text && <div className="mt-1 opacity-90">“{f.problematic_text}”</div>}
            <div className="mt-1 opacity-75">
              {f.type === 'outdated_future' ? f.explanation : words(f)[1]}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Specific articles' count line: "Showing 50 of 1,013 read articles",
 * "All 12 read articles", "3 match “titan”", "No read article matches “x”".
 */
export function sourceCountWords(shown: number, total: number, search: string): string {
  const n = (k: number) => k.toLocaleString('en-US');
  if (search) {
    if (!total) return `No read article matches “${search}”`;
    const what = `${n(total)} ${total === 1 ? 'matches' : 'match'} “${search}”`;
    return shown < total ? `${what} · showing ${n(shown)}` : what;
  }
  if (!total) return 'Prism AI hasn’t read any articles yet';
  if (shown < total) return `Showing ${n(shown)} of ${n(total)} read articles`;
  return total === 1 ? 'The 1 read article' : `All ${n(total)} read articles`;
}
